import localization from '../../../shared/strings';
import { isMasterSelectionContext } from './selectionContext';
import { parseSelectionKey } from './selectionKey';

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;

const buildVariantDisplayName = ({
  productTitle = '',
  availableOptions = [],
  variantOptions = {},
}) => {
  const optionValues = availableOptions
    .map(({ name }) => variantOptions[name])
    .filter((value) => value != null && value !== '');

  if (!optionValues.length) return productTitle || '';

  return `${productTitle} - ${[...optionValues].join(' / ')}`;
};

const resolveMasterVariantEntries = (state, selectionKeys, selectionByKey) => {
  const entries = [];

  selectionKeys.forEach((key) => {
    const entry = selectionByKey[key];
    if (!entry || entry.type !== 'masterVariant') return;

    const { _id } = parseSelectionKey(key);
    state.products.items.forEach((product) => {
      const variant = product.variants?.find((item) => String(item._id) === String(_id));
      if (!variant) return;

      entries.push({
        variant,
        parent: product,
        productId: String(product._id),
      });
    });
  });

  return entries;
};

const resolveListingVariantEntries = (state, selectionKeys, selectionByKey) => {
  const entities = state.products.entities || {};
  const entries = [];

  selectionKeys.forEach((key) => {
    const entry = selectionByKey[key];
    if (!entry || entry.type !== 'listingVariant') return;

    const { _id } = parseSelectionKey(key);
    const listingVariant = entities.listingVariants?.[_id];
    if (!listingVariant) return;

    const parentListing = Object.values(entities.listings || {}).find(
      (listing) => listing?._id === listingVariant.listingProductId
        || listing?._id === listingVariant.productId,
    );

    entries.push({
      variant: listingVariant,
      parent: parentListing || {},
      productId: String(parentListing?._id || listingVariant.listingProductId || ''),
    });
  });

  return entries;
};

const SELECTED_PRODUCT_TYPES = new Set(['master', 'listing']);

const collectSelectedProductIds = (selectionKeys, selectionByKey) => {
  const ids = [];
  selectionKeys.forEach((key) => {
    const entry = selectionByKey[key];
    if (!entry || !SELECTED_PRODUCT_TYPES.has(entry.type)) return;
    ids.push(String(parseSelectionKey(key)._id));
  });
  return ids;
};

const resolveParentTitle = (entries, singleParent) => {
  if (!singleParent) return aggregatedL10n.multipleValues;
  return entries[0]?.parent?.title || '';
};

const resolveParentSku = (entries, singleParent) => {
  if (!singleParent) return aggregatedL10n.multipleValues;
  return entries[0]?.parent?.sku || '';
};

const MIXED_PARENT_STATUS = 'MIXED';

const resolveParentStatus = (entries) => {
  const statuses = [...new Set(
    entries.map((entry) => String(entry?.parent?.status || 'draft').toUpperCase()),
  )];

  if (statuses.length !== 1) return MIXED_PARENT_STATUS;
  return statuses[0];
};

const resolveVariantDisplayName = (entries) => {
  if (entries.length >= 2) {
    return aggregatedL10n.selectedVariantsCount(entries.length);
  }

  const { variant, parent } = entries[0];
  return variant.displayName || buildVariantDisplayName({
    productTitle: parent?.title || '',
    availableOptions: parent?.availableOptions || [],
    variantOptions: variant.options || {},
  });
};

export const resolveAggregatedVariantContext = (state) => {
  const { ui: { selection } } = state.products;
  const {
    context,
    keys: selectionKeys,
    byKey: selectionByKey,
  } = selection;

  const entries = isMasterSelectionContext(context)
    ? resolveMasterVariantEntries(state, selectionKeys, selectionByKey)
    : resolveListingVariantEntries(state, selectionKeys, selectionByKey);

  if (!entries.length) return null;

  const parentIds = new Set(entries.map((entry) => entry.productId).filter(Boolean));
  const selectedProductIds = collectSelectedProductIds(selectionKeys, selectionByKey);
  const relatedProductIds = new Set([...parentIds, ...selectedProductIds]);
  const singleParent = parentIds.size === 1 && relatedProductIds.size === 1;
  const first = entries[0];

  return {
    productTitle: resolveParentTitle(entries, singleParent),
    parentSku: resolveParentSku(entries, singleParent),
    parentStatus: resolveParentStatus(entries),
    availableOptions: first.parent?.availableOptions || [],
    variantOptions: entries.length === 1 ? (first.variant?.options || {}) : {},
    variantDisplayName: resolveVariantDisplayName(entries),
    navigateProductId: singleParent ? first.productId : null,
  };
};

export default resolveAggregatedVariantContext;
